/* ============================================================
   Enira — theme.js
   Cart (AJAX + Section Rendering API), cart drawer, mobile nav,
   hero slideshow, shop-the-look, bundle builder, PDP gallery,
   qty steppers, accordions, collection filters/sort/price,
   sticky add-to-cart bar, toast. Vanilla, no dependencies.

   Reads the globals written by layout/theme.liquid:
     window.Enira = { routes, strings, cart, moneyFormat }
   and exposes helpers back on the same object:
     Enira.toast() Enira.openCart() Enira.closeCart()
     Enira.refreshCart() Enira.formatMoney() Enira.init()
============================================================ */
(function () {
  'use strict';

  var Enira = window.Enira || (window.Enira = {});
  var routes = Enira.routes || (Enira.routes = {});
  var strings = Enira.strings || (Enira.strings = {});
  var cartCfg = Enira.cart || (Enira.cart = {});

  var cartUrl = routes.cart_url || '/cart';
  var addUrl = routes.cart_add_url || '/cart/add';
  var changeUrl = routes.cart_change_url || '/cart/change';
  var updateUrl = routes.cart_update_url || '/cart/update';

  var DEFAULT_STRINGS = {
    added: 'Added to cart',
    soldOut: 'Sold out',
    bundlePrompt: 'Pick {n} pieces first',
    bundleAdded: 'Bundle added — {d}% off',
    codeCopied: 'COPIED ✓',
    freeShip: 'You are {x} away from free shipping',
    freeShipMet: 'Congratulations! You are eligible for free shipping!'
  };

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  var uid = 0;

  /* ============================================================
     Utilities
  ============================================================ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function $all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* Like $all, but also matches the scope element itself. */
  function scoped(sel, scope) {
    var root = scope || document;
    var list = $all(sel, root);
    if (root !== document && root.matches && root.matches(sel) && list.indexOf(root) === -1) {
      list.unshift(root);
    }
    return list;
  }

  function attr(el, name, fallback) {
    if (!el || !el.getAttribute) return fallback;
    var v = el.getAttribute(name);
    return (v === null || v === '') ? fallback : v;
  }

  function num(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }

  function str(key) {
    var v = strings[key];
    return (v === undefined || v === null || v === '') ? (DEFAULT_STRINGS[key] || '') : v;
  }

  /* "Add {n} more" + { n: 2 } → "Add 2 more". Unknown keys are left alone. */
  function format(template, map) {
    if (!template) return '';
    return String(template).replace(/\{(\w+)\}/g, function (whole, key) {
      return (map && Object.prototype.hasOwnProperty.call(map, key)) ? map[key] : whole;
    });
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isSmall() { return window.innerWidth <= 749; }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* Shopify's canonical money formatter, driven by shop.money_format. */
  function formatMoney(cents, moneyFormat) {
    var fmt = moneyFormat || Enira.moneyFormat || '${{amount}}';
    var value = cents;
    if (typeof value === 'string') value = value.replace('.', '');
    value = parseInt(value, 10);
    if (isNaN(value)) value = 0;

    function delimit(number, precision, thousands, decimal) {
      if (precision === undefined || precision === null) precision = 2;
      if (thousands === undefined || thousands === null) thousands = ',';
      if (decimal === undefined || decimal === null) decimal = '.';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    var out;
    switch (match && match[1]) {
      case 'amount_no_decimals': out = delimit(value, 0); break;
      case 'amount_with_comma_separator': out = delimit(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = delimit(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = delimit(value, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = delimit(value, 0, ' ', '.'); break;
      case 'amount_with_space_separator': out = delimit(value, 2, ' ', ','); break;
      default: out = delimit(value, 2);
    }
    return fmt.replace(placeholder, out);
  }

  /* ============================================================
     Toast — 1.9s auto-hide, mirrors the prototype
  ============================================================ */
  var toastTimer = null;

  function toast(message) {
    if (!message) return;
    var el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-on');
    }, num(attr(el, 'data-duration', ''), 1900));
  }

  /* ============================================================
     Focus management (drawer + mobile nav)
  ============================================================ */
  var activeTrap = null;

  function focusables(root) {
    return $all(FOCUSABLE, root).filter(function (el) {
      if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  function openTrap(root, opener) {
    if (!root) return;
    activeTrap = { root: root, opener: opener && opener.focus ? opener : document.activeElement };
    setTimeout(function () {
      if (!activeTrap || activeTrap.root !== root || !document.contains(root)) return;
      var target = root.querySelector('[data-trap-initial]') || focusables(root)[0];
      if (target && target.focus) {
        target.focus();
      } else if (root.focus) {
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
        root.focus();
      }
    }, 60);
  }

  function releaseTrap(root) {
    if (!activeTrap) return;
    if (root && activeTrap.root !== root) return;
    var opener = activeTrap.opener;
    activeTrap = null;
    if (opener && opener.focus && document.contains(opener)) {
      try { opener.focus(); } catch (e) { /* element may be gone */ }
    }
  }

  /* ============================================================
     Cart drawer
  ============================================================ */
  var cartOpener = null;

  function cartSection() { return $('#shopify-section-cart-drawer'); }

  function drawerEl() {
    var s = cartSection();
    return (s && s.querySelector('.drawer')) || $('.drawer');
  }

  function drawerDim() {
    var s = cartSection();
    return (s && s.querySelector('.drawer-dim')) || $('.drawer-dim');
  }

  function drawerIsOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('is-open'));
  }

  function openCart(opener) {
    var d = drawerEl();
    if (!d) { window.location.href = cartUrl; return; }
    closeMenu(true);
    if (opener) cartOpener = opener;
    var dim = drawerDim();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    if (dim) dim.classList.add('is-open');
    document.body.classList.add('is-locked');
    openTrap(d, cartOpener);
  }

  /* keepFocus === true drops the trap without moving focus back to the opener
     (used when one overlay hands over to another). */
  function closeCart(keepFocus) {
    var d = drawerEl();
    var dim = drawerDim();
    if (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    }
    if (dim) dim.classList.remove('is-open');
    if (!menuIsOpen()) document.body.classList.remove('is-locked');
    if (keepFocus === true) {
      if (activeTrap && activeTrap.root === d) activeTrap = null;
    } else {
      releaseTrap(d);
    }
  }

  /* Section ids to re-render on every cart mutation. The drawer is rendered
     from the layout so its id is stable; the cart page opts in by putting
     data-cart-section-id="{{ section.id }}" on its root element. */
  function cartSectionIds() {
    var ids = ['cart-drawer'];
    $all('[data-cart-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function refreshCartSection() {
    var wasOpen = drawerIsOpen();
    var ids = cartSectionIds();
    return fetch(cartUrl + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json) return;
        ids.forEach(function (id) {
          var html = json[id];
          if (!html) return;
          var target = document.getElementById('shopify-section-' + id);
          if (target) target.outerHTML = html;
        });
        if (wasOpen) openCart();
        syncCount();
        updateShipMeters();
        initAll(document);
      })
      .catch(function () { /* leave the current DOM in place */ });
  }

  function syncCount() {
    var source = $('#shopify-section-cart-drawer [data-cart-count]') || $('[data-cart-count]');
    if (!source) return;
    var n = source.getAttribute('data-cart-count');
    if (n === null) return;
    $all('.cart-count').forEach(function (el) { el.textContent = n; });
  }

  function cartError(err) {
    var msg = (err && (err.description || err.message)) || str('soldOut');
    toast(msg);
  }

  /* payload: { items: [{ id, quantity }] } or a FormData instance */
  function addToCart(payload, btn) {
    if (btn) btn.setAttribute('aria-busy', 'true');
    var opts = { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (payload instanceof FormData) {
      opts.headers.Accept = 'application/json';
      opts.body = payload;
    } else {
      opts.headers['Content-Type'] = 'application/json';
      opts.headers.Accept = 'application/json';
      opts.body = JSON.stringify(payload);
    }
    return fetch(addUrl + '.js', opts)
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () { return refreshCartSection(); })
      .then(function () {
        openCart(btn);
        toast(str('added'));
      })
      .catch(cartError)
      .then(function () {
        if (btn) btn.removeAttribute('aria-busy');
      });
  }

  function changeLine(key, quantity, btn) {
    if (!key) return Promise.resolve();
    if (btn) btn.setAttribute('aria-busy', 'true');
    return fetch(changeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () { return refreshCartSection(); })
      .catch(cartError)
      .then(function () { if (btn) btn.removeAttribute('aria-busy'); });
  }

  var saveNote = debounce(function (value) {
    fetch(updateUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ note: value })
    }).catch(function () { /* non-critical */ });
  }, 500);

  /* ---------------- Free-shipping meter ---------------- */
  function updateShipMeters() {
    var threshold = num(cartCfg.freeShippingThreshold, 0);
    $all('[data-ship-meter]').forEach(function (meter) {
      var total = num(attr(meter, 'data-total', ''), 0);
      var limit = num(attr(meter, 'data-threshold', ''), threshold);
      var msg = meter.querySelector('[data-ship-message]') || meter.querySelector('.ship-meter__msg');
      var fill = meter.querySelector('[data-ship-fill]') || meter.querySelector('.ship-meter__fill');
      var met = limit <= 0 || total >= limit;
      var pct = limit > 0 ? Math.min(100, Math.round((total / limit) * 100)) : 100;
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        msg.textContent = met
          ? str('freeShipMet')
          : format(str('freeShip'), { x: formatMoney(limit - total) });
      }
      meter.classList.toggle('is-met', met);
      var bar = meter.querySelector('[data-ship-bar]') || meter.querySelector('.ship-meter__bar');
      if (bar) {
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-valuemin', '0');
        bar.setAttribute('aria-valuemax', '100');
        bar.setAttribute('aria-valuenow', String(pct));
      }
    });
  }

  /* ============================================================
     Mobile navigation
  ============================================================ */
  function menuEl() { return $('.enira-mobile-nav'); }
  function menuDim() { return $('.enira-mobile-dim'); }
  function menuIsOpen() {
    var m = menuEl();
    return !!(m && m.classList.contains('is-open'));
  }

  function openMenu(opener) {
    var m = menuEl();
    if (!m) return;
    var dim = menuDim();
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    if (dim) dim.classList.add('is-open');
    document.body.classList.add('is-locked');
    $all('[data-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    openTrap(m, opener);
  }

  function closeMenu(keepFocus) {
    var m = menuEl();
    if (!m) return;
    var dim = menuDim();
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    if (dim) dim.classList.remove('is-open');
    if (!drawerIsOpen()) document.body.classList.remove('is-locked');
    $all('[data-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (keepFocus === true) {
      if (activeTrap && activeTrap.root === m) activeTrap = null;
    } else {
      releaseTrap(m);
    }
  }

  /* ============================================================
     Hero slideshow
  ============================================================ */
  function initHero(root) {
    if (!root || root._enira) return;

    var slides = $all('[data-hero-slide]', root);
    if (!slides.length) return;

    var dots = $all('[data-hero-dot]', root);
    var interval = Math.max(1200, num(attr(root, 'data-interval', ''), 5200));
    var canAutoplay = attr(root, 'data-autoplay', 'true') !== 'false' && slides.length > 1;
    var index = 0;
    var timer = null;
    var hovering = false;
    var focused = false;
    var editorHold = false;
    var startX = 0, startY = 0;

    function show(i) {
      var count = slides.length;
      index = ((i % count) + count) % count;
      slides.forEach(function (slide, k) {
        var on = k === index;
        slide.classList.toggle('is-on', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        $all('a, button', slide).forEach(function (el) {
          if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      dots.forEach(function (dot, k) {
        var on = num(attr(dot, 'data-index', ''), k) === index;
        dot.classList.toggle('is-on', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function start() {
      stop();
      if (!canAutoplay || reducedMotion() || hovering || focused || editorHold) return;
      if (document.hidden) return;
      timer = setInterval(function () { show(index + 1); }, interval);
    }

    root.addEventListener('mouseenter', function () { hovering = true; stop(); });
    root.addEventListener('mouseleave', function () { hovering = false; start(); });
    root.addEventListener('focusin', function () { focused = true; stop(); });
    root.addEventListener('focusout', function (e) {
      if (root.contains(e.relatedTarget)) return;
      focused = false;
      start();
    });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
    });
    root.addEventListener('touchstart', function (e) {
      var t = e.changedTouches[0];
      startX = t.clientX; startY = t.clientY;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var t = e.changedTouches[0];
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    root._enira = {
      type: 'hero',
      show: show,
      next: function () { show(index + 1); },
      prev: function () { show(index - 1); },
      start: start,
      stop: stop,
      hold: function (on) { editorHold = !!on; if (on) stop(); else start(); },
      slideAt: function (el) {
        var at = slides.indexOf(el);
        if (at > -1) show(at);
      },
      destroy: stop
    };

    show(0);
    start();
  }

  /* ============================================================
     Shop the look — cycles the linked product card
  ============================================================ */
  function initLook(root) {
    if (!root || root._enira) return;
    var items = $all('[data-look-item]', root);
    if (!items.length) return;
    var index = 0;

    function show(i) {
      var count = items.length;
      index = ((i % count) + count) % count;
      items.forEach(function (item, k) {
        var on = k === index;
        item.classList.toggle('is-on', on);
        item.hidden = !on;
        item.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      $all('[data-look-dot]', root).forEach(function (dot, k) {
        dot.classList.toggle('is-on', k === index);
      });
      var nav = root.querySelector('[data-look-position]');
      if (nav) nav.textContent = (index + 1) + '/' + items.length;
    }

    root._enira = {
      type: 'look',
      show: show,
      next: function () { show(index + 1); },
      prev: function () { show(index - 1); },
      itemAt: function (el) {
        var at = items.indexOf(el);
        if (at > -1) show(at);
      },
      destroy: function () {}
    };

    show(0);
  }

  /* ============================================================
     Bundle builder
  ============================================================ */
  function bundleToggles(root) { return $all('[data-bundle-toggle]', root); }

  function bundleSelected(root) {
    return bundleToggles(root).filter(function (t) {
      return t.getAttribute('aria-pressed') === 'true';
    });
  }

  function bundleCard(toggle) {
    return toggle.closest('.bundle__card') || toggle;
  }

  function paintBundle(root) {
    if (!root) return;
    var discount = Math.min(100, Math.max(0, num(attr(root, 'data-discount', ''), 30)));
    var required = Math.max(1, num(attr(root, 'data-required', attr(root, 'data-required-count', '')), 3));
    var picked = bundleSelected(root);
    var gross = picked.reduce(function (sum, t) { return sum + num(attr(t, 'data-price', ''), 0); }, 0);
    var total = Math.round(gross * (100 - discount) / 100);
    var met = picked.length >= required;

    bundleToggles(root).forEach(function (t) {
      var on = t.getAttribute('aria-pressed') === 'true';
      var card = bundleCard(t);
      card.classList.toggle('is-on', on);
      var mark = card.querySelector('[data-bundle-mark]') || card.querySelector('.bundle__mark');
      if (mark) {
        mark.textContent = on
          ? attr(mark, 'data-mark-on', '✓')
          : attr(mark, 'data-mark-off', '+');
      }
    });

    /* The copy templates live on the elements that render them, written by
       bundle-builder.liquid from section settings. The second name in each
       pair is the older root-level spelling, kept as a fallback. */
    var headline = root.querySelector('[data-bundle-headline]');
    if (headline) {
      var locked = attr(headline, 'data-headline-incomplete',
        attr(root, 'data-headline-locked', 'Add {n} more to get {d}% Off'));
      var unlocked = attr(headline, 'data-headline-complete',
        attr(root, 'data-headline-unlocked', '{d}% off unlocked'));
      headline.textContent = met
        ? format(unlocked, { d: discount, n: required })
        : format(locked, { n: required - picked.length, d: discount });
    }

    var status = root.querySelector('[data-bundle-status]');
    if (status) {
      var empty = attr(status, 'data-status-empty',
        attr(root, 'data-status-empty', 'Your bundle is empty.'));
      var partial = attr(status, 'data-status-partial',
        attr(root, 'data-status-selected', '{k} of {n} selected · {total}'));
      status.textContent = picked.length === 0
        ? empty
        : format(partial, { k: picked.length, n: required, d: discount, total: formatMoney(total) });
    }

    var cta = root.querySelector('[data-bundle-add]');
    if (cta) {
      cta.setAttribute('aria-disabled', met ? 'false' : 'true');
      cta.classList.toggle('is-ready', met);
    }
  }

  function initBundle(root) {
    if (!root || root._enira) return;
    if (!bundleToggles(root).length) return;
    bundleToggles(root).forEach(function (t) {
      if (!t.hasAttribute('aria-pressed')) {
        t.setAttribute('aria-pressed', bundleCard(t).classList.contains('is-on') ? 'true' : 'false');
      }
    });
    root._enira = { type: 'bundle', paint: function () { paintBundle(root); }, destroy: function () {} };
    paintBundle(root);
  }

  function addBundle(root, btn) {
    if (!root) return;
    var discount = num(attr(root, 'data-discount', ''), 30);
    var required = Math.max(1, num(attr(root, 'data-required', attr(root, 'data-required-count', '')), 3));
    var picked = bundleSelected(root);
    /* Per-section toast copy wins over the global strings when the merchant
       has set it (data-toast-incomplete / data-toast-added on the CTA). */
    var promptTpl = btn ? attr(btn, 'data-toast-incomplete', str('bundlePrompt')) : str('bundlePrompt');
    var addedTpl = btn ? attr(btn, 'data-toast-added', str('bundleAdded')) : str('bundleAdded');
    if (picked.length < required) {
      toast(format(promptTpl, { n: required, d: discount }));
      return;
    }
    var items = [];
    picked.forEach(function (t) {
      var id = num(attr(t, 'data-variant-id', ''), 0);
      if (!id) return;
      var at = -1;
      items.forEach(function (line, k) { if (line.id === id) at = k; });
      if (at > -1) items[at].quantity += 1;
      else items.push({ id: id, quantity: 1 });
    });
    if (!items.length) { toast(str('soldOut')); return; }

    if (btn) btn.setAttribute('aria-busy', 'true');
    fetch(addUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () { return refreshCartSection(); })
      .then(function () {
        bundleToggles(root).forEach(function (t) { t.setAttribute('aria-pressed', 'false'); });
        paintBundle(root);
        openCart(btn);
        toast(format(addedTpl, { d: discount, n: required }));
      })
      .catch(cartError)
      .then(function () { if (btn) btn.removeAttribute('aria-busy'); });
  }

  /* ============================================================
     Product gallery
  ============================================================ */
  function selectThumb(thumb) {
    var src = thumb.getAttribute('data-src');
    var main = $('#pdp-main-image');
    if (main && src) {
      var srcset = thumb.getAttribute('data-srcset');
      if (srcset) main.setAttribute('srcset', srcset);
      else main.removeAttribute('srcset');
      main.setAttribute('src', src);
      var alt = thumb.getAttribute('data-alt');
      if (alt !== null) main.setAttribute('alt', alt);
    }
    var group = thumb.closest('.pdp__thumbs') || document;
    $all('[data-gallery-thumb]', group).forEach(function (x) {
      x.classList.remove('is-on');
      x.setAttribute('aria-current', 'false');
    });
    thumb.classList.add('is-on');
    thumb.setAttribute('aria-current', 'true');
  }

  /* ============================================================
     Quantity steppers
  ============================================================ */
  function stepperInput(btn) {
    var target = btn.getAttribute('data-qty-target');
    if (target) {
      var byId = document.getElementById(target);
      if (byId) return byId;
    }
    var box = btn.closest('.qty') || btn.parentNode;
    return box ? box.querySelector('[data-qty-input]') : null;
  }

  function setQty(input, value) {
    if (!input) return;
    var min = num(input.getAttribute('min'), 1);
    var max = num(input.getAttribute('max'), 0);
    var next = isNaN(value) ? min : value;
    if (next < min) next = min;
    if (max > 0 && next > max) next = max;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ============================================================
     Copy discount code
  ============================================================ */
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', 'readonly');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject();
      } catch (e) { reject(e); }
    });
  }

  function copyCode(btn) {
    var code = btn.getAttribute('data-code');
    if (!code) return;
    var label = btn.querySelector('[data-copy-label]') || btn;
    var original = btn._eniraLabel || label.textContent;
    btn._eniraLabel = original;
    copyText(code).then(function () {
      label.textContent = attr(btn, 'data-copied-label', str('codeCopied'));
      btn.classList.add('is-copied');
      toast(format(attr(btn, 'data-copied-toast', str('codeCopied')), { code: code }));
      clearTimeout(btn._eniraTimer);
      btn._eniraTimer = setTimeout(function () {
        label.textContent = original;
        btn.classList.remove('is-copied');
      }, 2400);
    }).catch(function () {
      toast(code);
    });
  }

  /* ============================================================
     Accordions
  ============================================================ */
  function accBody(acc) {
    return acc.querySelector('[data-acc-body]') || acc.querySelector('.accordion__body');
  }

  function accHead(acc) {
    return acc.querySelector('[data-acc-head]') || acc.querySelector('.accordion__head');
  }

  function setAccordion(acc, open) {
    if (!acc) return;
    var head = accHead(acc);
    var body = accBody(acc);
    acc.classList.toggle('is-open', open);
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (body) {
      body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
      body.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
    var sign = acc.querySelector('[data-acc-sign]') || acc.querySelector('.accordion__sign');
    if (sign) {
      sign.textContent = open
        ? attr(sign, 'data-sign-open', '−')
        : attr(sign, 'data-sign-closed', '+');
    }
  }

  function initAccordion(acc) {
    if (!acc || acc._eniraAcc) return;
    acc._eniraAcc = true;
    var head = accHead(acc);
    var body = accBody(acc);
    if (head && body) {
      if (!body.id) body.id = 'enira-acc-' + (++uid);
      head.setAttribute('aria-controls', body.id);
      if (head.tagName === 'BUTTON' && !head.getAttribute('type')) head.setAttribute('type', 'button');
    }
    setAccordion(acc, acc.classList.contains('is-open'));
  }

  function toggleAccordion(acc) {
    var open = !acc.classList.contains('is-open');
    var group = acc.closest('[data-accordion-group]');
    if (group && open) {
      $all('[data-accordion]', group).forEach(function (other) {
        if (other !== acc) setAccordion(other, false);
      });
    }
    setAccordion(acc, open);
  }

  var resizeAccordions = debounce(function () {
    $all('[data-accordion].is-open').forEach(function (acc) { setAccordion(acc, true); });
  }, 150);

  /* ============================================================
     Collection: filters, sort, price range
  ============================================================ */
  function setFilters(panel, open) {
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.hidden = !open;
    panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    $all('[data-filter-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = t.querySelector('[data-filter-label]');
      if (label) {
        label.textContent = open
          ? attr(t, 'data-label-hide', 'Hide filter')
          : attr(t, 'data-label-show', 'Show filter');
      }
    });
  }

  function initFilters(panel) {
    if (!panel || panel._eniraFilters) return;
    panel._eniraFilters = true;
    panel._eniraWide = !isSmall();
    var forced = panel.getAttribute('data-open');
    var open = forced === 'true' ? true : (forced === 'false' ? false : !isSmall());
    setFilters(panel, open);
  }

  /* Crossing the mobile breakpoint restores the default state, unless the
     shopper has already made a choice on this page. */
  var resizeFilters = debounce(function () {
    var wide = !isSmall();
    $all('[data-filters]').forEach(function (panel) {
      if (!panel._eniraFilters || panel._eniraUserToggled) return;
      if (panel._eniraWide === wide) return;
      panel._eniraWide = wide;
      setFilters(panel, wide);
    });
  }, 150);

  function priceLabel(range) {
    var out = document.getElementById(attr(range, 'data-price-output', '')) ||
      $('[data-price-output]', range.closest('.price-range') || document) ||
      $('[data-price-output]');
    if (!out) return;
    var value = parseFloat(range.value);
    if (isNaN(value)) value = 0;
    var cents = attr(range, 'data-unit', 'major') === 'cents' ? Math.round(value) : Math.round(value * 100);
    out.textContent = formatMoney(cents);
  }

  function initPriceRange(range) {
    if (!range || range._eniraRange) return;
    range._eniraRange = true;
    priceLabel(range);
  }

  function applyPrice(range) {
    var url = new URL(window.location.href);
    var value = parseFloat(range.value);
    if (isNaN(value)) return;
    var param = attr(range, 'data-param', 'filter.v.price.lte');
    var max = parseFloat(range.getAttribute('max'));
    if (!isNaN(max) && value >= max) url.searchParams.delete(param);
    else url.searchParams.set(param, String(value));
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  /* ============================================================
     Sticky add-to-cart bar
  ============================================================ */
  function initStickyBar(bar) {
    if (!bar || bar._eniraSticky) return;

    /* Lets base.css keep the footer clear of the fixed bar on mobile. */
    document.body.classList.add('pdp-has-sticky');

    function reveal(on) {
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
    reveal(false);

    /* The anchor lives in the product section, which may not be in the DOM yet
       when this runs. Stay un-flagged so the next init pass retries. */
    var anchor = $('[data-atc-anchor]');
    if (!anchor) return;
    bar._eniraSticky = true;

    /* Single source of truth: the bar shows once the anchor is fully above
       the viewport. The observer is only the trigger. */
    function evaluate() {
      if (!document.contains(anchor)) return;
      reveal(anchor.getBoundingClientRect().bottom < 0);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function () { evaluate(); }, { threshold: 0 });
      io.observe(anchor);
      bar._eniraObserver = io;
    } else {
      window.addEventListener('scroll', evaluate, { passive: true });
    }
    /* Correct the initial state even if the first observer callback is late
       (setTimeout rather than rAF so it still runs in a background tab). */
    setTimeout(evaluate, 0);
  }

  /* ============================================================
     Announcement bar — message rotation + live countdown
     Hooks: [data-announce][data-interval] root, .announce__text messages,
            [data-countdown][data-until] clock, [data-hide-expired] on the
            message that owns the clock. Expired messages get data-expired.
  ============================================================ */
  function initAnnounce(root) {
    if (!root || root._enira) return;

    var items = $all('.announce__text', root);
    if (!items.length) return;

    var clocks = $all('[data-countdown]', root);
    var interval = Math.max(2000, num(attr(root, 'data-interval', ''), 5000));
    var timers = [];
    var paused = false;
    var current = 0;

    for (var k = 0; k < items.length; k++) {
      if (!items[k].hasAttribute('hidden')) { current = k; break; }
    }

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    /* Accepts "2026-12-31 23:59" as well as the ISO form Safari insists on. */
    function endOf(el) {
      var raw = attr(el, 'data-until', '');
      var ms = Date.parse(raw);
      if (isNaN(ms)) ms = Date.parse(raw.replace(' ', 'T'));
      return ms;
    }

    function tick() {
      var now = Date.now();
      clocks.forEach(function (el) {
        var end = endOf(el);
        if (isNaN(end)) { el.textContent = ''; return; }
        var left = Math.floor((end - now) / 1000);
        if (left <= 0) {
          el.textContent = '0D 00 : 00 : 00';
          var host = (el.closest && el.closest('.announce__text')) || el.parentNode;
          if (host && host.getAttribute('data-hide-expired') === 'true') {
            host.setAttribute('data-expired', 'true');
            host.setAttribute('hidden', 'hidden');
          }
          return;
        }
        el.textContent = Math.floor(left / 86400) + 'D ' +
          pad(Math.floor((left % 86400) / 3600)) + ' : ' +
          pad(Math.floor((left % 3600) / 60)) + ' : ' +
          pad(left % 60);
      });

      /* Every message has run out — retire the whole bar. */
      var alive = 0;
      items.forEach(function (i) {
        if (i.getAttribute('data-expired') !== 'true') alive++;
      });
      if (!alive) root.setAttribute('hidden', 'hidden');
      else root.removeAttribute('hidden');
    }

    function advance() {
      if (paused) return;
      var next = current;
      for (var i = 0; i < items.length; i++) {
        next = (next + 1) % items.length;
        if (items[next].getAttribute('data-expired') !== 'true') break;
      }
      if (next === current) return;
      items[current].setAttribute('hidden', 'hidden');
      items[next].removeAttribute('hidden');
      current = next;
    }

    if (clocks.length) {
      tick();
      timers.push(setInterval(tick, 1000));
    }

    if (items.length > 1 && !reducedMotion()) {
      timers.push(setInterval(advance, interval));
      root.addEventListener('mouseenter', function () { paused = true; });
      root.addEventListener('mouseleave', function () { paused = false; });
      root.addEventListener('focusin', function () { paused = true; });
      root.addEventListener('focusout', function () { paused = false; });
    }

    root._enira = {
      type: 'announce',
      destroy: function () {
        timers.forEach(function (t) { clearInterval(t); });
        timers = [];
      }
    };
  }

  /* ============================================================
     Product variant picker (PDP)
     Repaints price, stock, buttons, gallery and the address bar when the
     shopper changes an option. Variant JSON comes from [data-variant-data]
     inside the same section; strings come off .pdp.
  ============================================================ */
  function initProduct(picker) {
    if (!picker || picker._enira) return;

    var root = picker.closest('.shopify-section') || document;
    var dataEl = root.querySelector('[data-variant-data]');
    var variants = [];
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; }
    }

    var groups = $all('[data-variant-group]', picker);
    if (!groups.length || !variants.length) return;
    picker._enira = { type: 'product', destroy: function () {} };

    var pdp = root.querySelector('.pdp');
    var idInput = root.querySelector('[data-variant-id-input]');
    var priceBox = root.querySelector('[data-pdp-price]');
    var stickyAdd = root.querySelector('[data-sticky-add]');
    var atcButton = root.querySelector('[data-atc-button]');
    var mainImage = root.querySelector('#pdp-main-image');

    var S = {
      add: attr(pdp, 'data-str-add', 'Add to cart'),
      soldOut: attr(pdp, 'data-str-sold-out', str('soldOut')),
      unavailable: attr(pdp, 'data-str-unavailable', 'Unavailable')
    };

    function groupValue(group) {
      var select = group.querySelector('select[data-variant-option]');
      if (select) return select.value;
      var checked = group.querySelector('input[data-variant-option]:checked');
      return checked ? checked.value : null;
    }

    function currentValues() {
      return groups.map(groupValue);
    }

    function matches(variant, values) {
      for (var i = 0; i < values.length; i++) {
        if (values[i] === null || values[i] === undefined) continue;
        if (String(variant.options[i]) !== String(values[i])) return false;
      }
      return true;
    }

    function findVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        if (matches(variants[i], values)) return variants[i];
      }
      return null;
    }

    function anyAvailable(values) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available && matches(variants[i], values)) return true;
      }
      return false;
    }

    function paintLabels(values) {
      var fmt = attr(picker, 'data-label-format', '{option} · {value}');
      groups.forEach(function (group, index) {
        var label = group.querySelector('[data-variant-label]');
        if (!label) return;
        label.textContent = format(fmt, {
          option: attr(label, 'data-option-name', ''),
          value: values[index] === null || values[index] === undefined ? '' : values[index]
        });
      });
    }

    function paintSwatches(values) {
      groups.forEach(function (group, index) {
        $all('input[data-variant-option]', group).forEach(function (input) {
          var swatch = input.parentNode;
          if (!swatch || !swatch.classList) return;
          swatch.classList.toggle('is-on', input.checked);
          var probe = values.slice();
          probe[index] = input.value;
          swatch.classList.toggle('is-unavailable', !anyAvailable(probe));
        });
      });
    }

    function paintPrice(variant) {
      if (!priceBox || !variant) return;
      var current = priceBox.querySelector('[data-price-current]');
      var compare = priceBox.querySelector('[data-price-compare]');
      var wrap = priceBox.querySelector('.price');
      var onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      if (current) current.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (wrap) wrap.classList.toggle('price--on-sale', onSale);
    }

    function paintStock(variant) {
      var stock = root.querySelector('[data-stock]');
      if (!stock) return;
      var available = !!(variant && variant.available);
      stock.classList.toggle('stock--out', !available);
      var label = stock.querySelector('[data-stock-label]');
      if (label) {
        label.textContent = available
          ? attr(stock, 'data-in-label', '')
          : attr(stock, 'data-out-label', '');
      }
    }

    function paintButtons(variant) {
      var label = variant ? (variant.available ? S.add : S.soldOut) : S.unavailable;
      var buyable = !!(variant && variant.available);
      if (atcButton) {
        atcButton.disabled = !buyable;
        var span = atcButton.querySelector('[data-atc-label]');
        if (span) span.textContent = label;
        else atcButton.textContent = label;
      }
      if (stickyAdd) {
        stickyAdd.disabled = !buyable;
        stickyAdd.textContent = label;
        stickyAdd.setAttribute('data-quick-add', variant ? String(variant.id) : '');
      }
      var stickyPrice = root.querySelector('[data-sticky-price]');
      if (stickyPrice && variant) stickyPrice.textContent = formatMoney(variant.price);
    }

    function paintImage(variant) {
      if (!mainImage || !variant) return;
      /* Prefer the media id — that is what the thumbnails carry. Routing through
         selectThumb keeps srcset, alt and .is-on consistent with a real click. */
      var mediaId = variant.featured_media ? variant.featured_media.id : null;
      if (mediaId) {
        var thumb = root.querySelector('[data-gallery-thumb][data-media-id="' + mediaId + '"]');
        if (thumb) { selectThumb(thumb); return; }
      }
      var img = variant.featured_image;
      if (!img || !img.src) return;
      mainImage.removeAttribute('srcset');
      mainImage.setAttribute('src', img.src);
      if (img.alt) mainImage.setAttribute('alt', img.alt);
    }

    function paintUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var base = attr(picker, 'data-url', '');
      if (!base) return;
      window.history.replaceState({}, '', base + '?variant=' + variant.id);
    }

    function update() {
      var values = currentValues();
      var variant = findVariant(values);
      paintLabels(values);
      paintSwatches(values);
      paintPrice(variant);
      paintStock(variant);
      paintButtons(variant);
      if (idInput) {
        idInput.value = variant ? String(variant.id) : '';
        idInput.disabled = !variant;
      }
      if (variant) {
        paintImage(variant);
        paintUrl(variant);
      }
    }

    picker._eniraUpdate = update;
    /* First paint marks the unavailable combinations. */
    update();
  }

  /* ============================================================
     Related products — upgrade the collection fallback with Shopify's
     recommendations endpoint. [data-related][data-url], [data-related-grid].
  ============================================================ */
  function initRelated(box) {
    if (!box || box._enira) return;
    var url = attr(box, 'data-url', '');
    if (!url || typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') return;
    box._enira = { type: 'related', destroy: function () {} };

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-related]');
        if (!fresh) return;
        var grid = fresh.querySelector('[data-related-grid]');
        /* Nothing to suggest — leave the collection fallback alone. */
        if (!grid || !grid.children.length) return;
        box.innerHTML = fresh.innerHTML;
        box.hidden = false;
        initAll(box);
      })
      .catch(function () { /* network or 404: the fallback is already rendered */ });
  }

  /* ============================================================
     Delegated events
  ============================================================ */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el;

    /* ---- Cart drawer ---- */
    if ((el = t.closest('[data-cart-open]'))) { e.preventDefault(); openCart(el); return; }
    if (t.closest('[data-cart-close]')) { e.preventDefault(); closeCart(); return; }

    /* ---- Cart lines ---- */
    if ((el = t.closest('[data-line-remove]'))) {
      e.preventDefault();
      changeLine(el.getAttribute('data-key'), 0, el);
      return;
    }
    if ((el = t.closest('[data-line-change]'))) {
      e.preventDefault();
      var q = num(el.getAttribute('data-qty'), -1);
      if (q >= 0) changeLine(el.getAttribute('data-key'), q, el);
      return;
    }

    /* ---- Quick add (value = variant id) ---- */
    if ((el = t.closest('[data-quick-add]'))) {
      e.preventDefault();
      var vid = num(el.getAttribute('data-quick-add'), 0);
      if (!vid) { toast(str('soldOut')); return; }
      addToCart({ items: [{ id: vid, quantity: num(el.getAttribute('data-quantity'), 1) }] }, el);
      return;
    }

    /* ---- Mobile nav ---- */
    if ((el = t.closest('[data-menu-open]'))) { e.preventDefault(); openMenu(el); return; }
    if (t.closest('[data-menu-close]')) { e.preventDefault(); closeMenu(); return; }

    /* ---- Hero slideshow ---- */
    if ((el = t.closest('[data-hero-prev]'))) {
      e.preventDefault();
      var hp = el.closest('[data-hero]');
      if (hp && hp._enira) hp._enira.prev();
      return;
    }
    if ((el = t.closest('[data-hero-next]'))) {
      e.preventDefault();
      var hn = el.closest('[data-hero]');
      if (hn && hn._enira) hn._enira.next();
      return;
    }
    if ((el = t.closest('[data-hero-dot]'))) {
      e.preventDefault();
      var hd = el.closest('[data-hero]');
      if (hd && hd._enira) hd._enira.show(num(el.getAttribute('data-index'), 0));
      return;
    }

    /* ---- Shop the look ---- */
    if ((el = t.closest('[data-look-prev]'))) {
      e.preventDefault();
      var lp = el.closest('[data-look]');
      if (lp && lp._enira) lp._enira.prev();
      return;
    }
    if ((el = t.closest('[data-look-next]'))) {
      e.preventDefault();
      var ln = el.closest('[data-look]');
      if (ln && ln._enira) ln._enira.next();
      return;
    }

    /* ---- Bundle builder ---- */
    if ((el = t.closest('[data-bundle-toggle]'))) {
      e.preventDefault();
      var broot = el.closest('[data-bundle]');
      el.setAttribute('aria-pressed', el.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      paintBundle(broot);
      return;
    }
    if ((el = t.closest('[data-bundle-add]'))) {
      e.preventDefault();
      addBundle(el.closest('[data-bundle]'), el);
      return;
    }

    /* ---- Product gallery ---- */
    if ((el = t.closest('[data-gallery-thumb]'))) { e.preventDefault(); selectThumb(el); return; }

    /* ---- Quantity steppers ---- */
    if ((el = t.closest('[data-qty-minus]'))) {
      e.preventDefault();
      var minusInput = stepperInput(el);
      if (minusInput) setQty(minusInput, num(minusInput.value, 1) - 1);
      return;
    }
    if ((el = t.closest('[data-qty-plus]'))) {
      e.preventDefault();
      var plusInput = stepperInput(el);
      if (plusInput) setQty(plusInput, num(plusInput.value, 1) + 1);
      return;
    }

    /* ---- Copy discount code ---- */
    if ((el = t.closest('[data-copy-code]'))) { e.preventDefault(); copyCode(el); return; }

    /* ---- Accordions ---- */
    if ((el = t.closest('[data-acc-head]'))) {
      var acc = el.closest('[data-accordion]');
      if (acc) { e.preventDefault(); toggleAccordion(acc); }
      return;
    }

    /* ---- Collection filter column ---- */
    if ((el = t.closest('[data-filter-toggle]'))) {
      e.preventDefault();
      var panel = document.getElementById(attr(el, 'data-filter-target', '')) || $('[data-filters]');
      if (panel) {
        panel._eniraUserToggled = true;
        setFilters(panel, panel.hidden);
      }
      return;
    }

    /* ---- Cart drawer tabs (Order note / Shipping / Discount) ---- */
    if ((el = t.closest('[data-drawer-tab]'))) {
      e.preventDefault();
      var tabs = el.closest('.drawer__tabs') || el.parentNode;
      var name = el.getAttribute('data-drawer-tab');
      var alreadyOn = el.getAttribute('aria-selected') === 'true';
      $all('[data-drawer-tab]', tabs).forEach(function (tab) {
        tab.setAttribute('aria-selected', 'false');
        tab.classList.remove('is-on');
      });
      $all('[data-drawer-panel]').forEach(function (p) {
        p.hidden = true;
      });
      if (!alreadyOn) {
        el.setAttribute('aria-selected', 'true');
        el.classList.add('is-on');
        var panelEl = $('[data-drawer-panel="' + name + '"]');
        if (panelEl) {
          panelEl.hidden = false;
          var field = panelEl.querySelector('textarea, input');
          if (field) field.focus();
        }
      }
      return;
    }
  });

  /* Add-to-cart forms (PDP, quick-add forms) → AJAX */
  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest ? e.target.closest('form[action*="/cart/add"]') : null;
    if (!form) return;
    /* Let the native "Buy it now" / checkout submit through. */
    var submitter = e.submitter || form.querySelector('[type="submit"]:focus');
    if (submitter && submitter.getAttribute('name') === 'checkout') return;
    if (form.hasAttribute('data-no-ajax')) return;
    e.preventDefault();
    addToCart(new FormData(form), form.querySelector('[type="submit"]'));
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el;

    /* Sort dropdown */
    if ((el = t.closest('[data-sort]'))) {
      var sortUrl = new URL(window.location.href);
      sortUrl.searchParams.set('sort_by', el.value);
      sortUrl.searchParams.delete('page');
      window.location.href = sortUrl.toString();
      return;
    }

    /* Price range → reload with the price filter applied */
    if ((el = t.closest('[data-price-range]'))) { priceLabel(el); applyPrice(el); return; }

    /* Variant option (swatch radio or select) → repaint the PDP */
    if ((el = t.closest('[data-variant-option]'))) {
      var picker = el.closest('[data-variant-picker]');
      if (picker && picker._eniraUpdate) picker._eniraUpdate();
      return;
    }

    /* Quantity input typed directly */
    if ((el = t.closest('[data-qty-input]'))) {
      var min = num(el.getAttribute('min'), 1);
      var v = num(el.value, min);
      if (v < min) { el.value = String(min); }
      var maxAttr = num(el.getAttribute('max'), 0);
      if (maxAttr > 0 && v > maxAttr) el.value = String(maxAttr);
      /* Keep the sticky bar's quick-add in step with the stepper. */
      var qtySection = el.closest('.shopify-section');
      var qtySticky = (qtySection || document).querySelector('[data-sticky-add]');
      if (qtySticky) qtySticky.setAttribute('data-quantity', el.value || '1');
      return;
    }

    /* Cart note */
    if ((el = t.closest('[data-cart-note]'))) { saveNote(el.value); return; }

    /* Filter checkboxes / swatches inside a filter form submit themselves */
    if ((el = t.closest('[data-filter-auto-submit]'))) {
      var form = el.closest('form');
      if (form) form.submit();
      return;
    }
  });

  /* Live label while dragging the price slider */
  document.addEventListener('input', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-price-range]') : null;
    if (el) priceLabel(el);
  });

  /* Keyboard: Escape closes overlays, Tab is trapped inside them */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (menuIsOpen()) { closeMenu(); return; }
      if (drawerIsOpen()) { closeCart(); return; }
      return;
    }
    if (e.key !== 'Tab' || !activeTrap) return;
    var root = activeTrap.root;
    if (!root || !document.contains(root)) { activeTrap = null; return; }
    var list = focusables(root);
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var inside = root.contains(document.activeElement);
    if (e.shiftKey) {
      if (!inside || document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else if (!inside || document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', function () {
    resizeAccordions();
    resizeFilters();
  });

  document.addEventListener('visibilitychange', function () {
    $all('[data-hero]').forEach(function (h) {
      if (!h._enira) return;
      if (document.hidden) h._enira.stop();
      else h._enira.start();
    });
  });

  /* ============================================================
     Init
  ============================================================ */
  function initAll(scope) {
    scoped('[data-announce]', scope).forEach(initAnnounce);
    scoped('[data-hero]', scope).forEach(initHero);
    scoped('[data-look]', scope).forEach(initLook);
    scoped('[data-bundle]', scope).forEach(initBundle);
    scoped('[data-variant-picker]', scope).forEach(initProduct);
    scoped('[data-related]', scope).forEach(initRelated);
    scoped('[data-accordion]', scope).forEach(initAccordion);
    scoped('[data-sticky-bar]', scope).forEach(initStickyBar);
    scoped('[data-filters]', scope).forEach(initFilters);
    scoped('[data-price-range]', scope).forEach(initPriceRange);
    syncCount();
    updateShipMeters();
  }

  function boot() { initAll(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------------- Theme editor ---------------- */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    scoped('[data-announce], [data-hero], [data-look], [data-bundle], [data-variant-picker], [data-related]', e.target).forEach(function (el) {
      if (el._enira && el._enira.destroy) el._enira.destroy();
      el._enira = null;
    });
    scoped('[data-sticky-bar]', e.target).forEach(function (el) {
      if (el._eniraObserver) el._eniraObserver.disconnect();
    });
  });

  document.addEventListener('shopify:section:select', function (e) {
    initAll(e.target);
    scoped('[data-hero]', e.target).forEach(function (h) {
      if (h._enira) h._enira.hold(true);
    });
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    scoped('[data-hero]', e.target).forEach(function (h) {
      if (h._enira) h._enira.hold(false);
    });
  });

  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block || !block.closest) return;
    var hero = block.closest('[data-hero]');
    if (hero && hero._enira) {
      hero._enira.hold(true);
      hero._enira.slideAt(block.matches('[data-hero-slide]') ? block : block.closest('[data-hero-slide]'));
      return;
    }
    var look = block.closest('[data-look]');
    if (look && look._enira) {
      look._enira.itemAt(block.matches('[data-look-item]') ? block : block.closest('[data-look-item]'));
    }
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var block = e.target;
    if (!block || !block.closest) return;
    var hero = block.closest('[data-hero]');
    if (hero && hero._enira) hero._enira.hold(false);
  });

  /* ---------------- Public API ---------------- */
  Enira.toast = toast;
  Enira.openCart = openCart;
  Enira.closeCart = closeCart;
  Enira.openMenu = openMenu;
  Enira.closeMenu = closeMenu;
  Enira.refreshCart = refreshCartSection;
  Enira.addToCart = addToCart;
  Enira.changeLine = changeLine;
  Enira.formatMoney = formatMoney;
  Enira.updateShipMeters = updateShipMeters;
  Enira.init = initAll;
})();
